import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/default/button';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import { Checkbox } from '@atlaskit/checkbox/checkbox';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

const ChoreographerModal = ({ children, onClose, testId }) => {
	const { Modal } = useChoreographerAssets();

	return (
		<Modal onClose={onClose} width="small" testId={testId}>
			{children}
		</Modal>
	);
};

const i18nBase = defineMessages({
	cancelDiscardModalButtonLabel: {
		id: 'import-from-sources.discard-modal.cancelAction',
		defaultMessage: 'Cancel',
		description: "Cancel button title in the 'import new document' modal",
	},
	createDiscardButtonLabel: {
		id: 'import-from-sources.discard-modal.createAction',
		defaultMessage: 'Create',
		description: "Confirm button title in the 'import new document' modal",
	},
	discardDraftDiscardModalCheckboxLabel: {
		id: 'import-from-sources.discard-modal.discardDraftAction',
		defaultMessage: 'Discard draft',
		description: "Label for 'discard' checkbox (asks if user wants to abandon the current changes)",
	},
	deleteCurrentPageCheckBoxLabel: {
		id: 'import-from-sources.discard-modal.live-page.discardDraftAction',
		defaultMessage: 'Delete current page',
		description:
			'The text to the right of a checkbox which allows a user to keep their content and create a new live page with the imported doc or overwrite their current content with the imported doc in the live page editor',
	},
	discardModalText: {
		id: 'import-from-sources.discard-modal.text',
		defaultMessage: 'Your new page won’t include any of the content in your current draft',
		description: 'warning (in a modal) that indicates user will lose all the current progress',
	},
	discardModalForLivePageText: {
		id: 'import-from-sources.discard-modal.live-page.text',
		defaultMessage: 'Your new page won’t include any of the content in your current page',
		description:
			'The text inside the body of the modal dialog that opens when a user choose to import a doc and already has content in the live page editor',
	},
	discardModalTitle: {
		id: 'import-from-sources.discard-modal.title',
		defaultMessage: 'Create a new page?',
		description: 'Modal title shown when the user tries to import document from a non-empty page',
	},
	discardModalLiveDocTitle: {
		id: 'import-from-sources.discard-modal.live-doc.title',
		defaultMessage: 'Create a new live doc?',
		description: 'Modal title shown when the user tries to import document from a non-empty page',
	},
});

const i18nLiveDocs = defineMessages({
	discardModalForLivePageText: {
		id: 'import-from-sources.discard-modal.live-page.text.livedocs',
		defaultMessage: 'Your new doc won’t include any of the content in your current doc',
		description: 'Updated version of import-from-sources.discard-modal.live-page.text',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalText = styled.p({
	marginBottom: token('space.200'),
});

type DiscardModalProps = {
	onClose: () => void;
	onCreate: (isDraftDiscarded: boolean) => void;
};

export const DiscardModal: React.FC<DiscardModalProps> = ({ onClose, onCreate }) => {
	const [isDraftDiscarded, setIsDraftDiscarded] = useState(false);
	const isLivePage = useIsCurrentPageLive();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'importDiscardModal',
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const discardChangeHandler = ({ target }) => {
		setIsDraftDiscarded(target.checked);
	};

	const handleDismiss = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'discardModal',
				action: 'dismissed',
				actionSubject: 'modal',
				actionSubjectId: 'importDiscardModal',
			},
		}).fire();
		onClose();
	};

	const handleCancel = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'discardModal',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'importDiscardModalCancel',
			},
		}).fire();
		onClose();
	};

	const handleCreate = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'discardModal',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'importDiscardModalCreate',
			},
		}).fire();
		onCreate(isDraftDiscarded);
	};

	return (
		<ChoreographerModal onClose={handleDismiss} testId="import-discard-modal">
			<ModalHeader hasCloseButton>
				<ModalTitle>
					<FormattedMessage
						{...(isLivePage ? i18n.discardModalLiveDocTitle : i18n.discardModalTitle)}
					/>
				</ModalTitle>
			</ModalHeader>

			<ModalBody>
				<ModalText>
					<FormattedMessage
						{...(isLivePage ? i18n.discardModalForLivePageText : i18n.discardModalText)}
					/>
				</ModalText>
				<Checkbox
					isChecked={isDraftDiscarded}
					label={
						<FormattedMessage
							{...(isLivePage
								? i18n.deleteCurrentPageCheckBoxLabel
								: i18n.discardDraftDiscardModalCheckboxLabel)}
						/>
					}
					onChange={discardChangeHandler}
				/>
			</ModalBody>
			<ModalFooter>
				<>
					<Button appearance="subtle" onClick={handleCancel} testId="discard-modal-close-button">
						<FormattedMessage {...i18n.cancelDiscardModalButtonLabel} />
					</Button>
					<Button appearance="primary" onClick={handleCreate} testId="discard-modal-create-button">
						<FormattedMessage {...i18n.createDiscardButtonLabel} />
					</Button>
				</>
			</ModalFooter>
		</ChoreographerModal>
	);
};
